import { useState } from 'react';
import {
  LayoutDashboard, Calendar, Users, FileText, Settings, Bell, Search, Plus,
  ChevronLeft, ChevronRight, Stethoscope, Clock, DollarSign, CreditCard,
  MoreHorizontal, Video, MapPin, RotateCcw, CheckCircle2, AlertCircle,
  AlertTriangle, Filter, X, Banknote, Smartphone, QrCode, Phone, MessageCircle
} from 'lucide-react';

// =================================================================
// RADIX COLORS — expanded palette (more functional colors)
// =================================================================
const SCALES = {
  slate:    ['#FCFCFD', '#F9F9FB', '#EFF0F3', '#E7E8EC', '#DFE0E4', '#D0D3D9', '#C3C6CD', '#B0B4BB', '#8B8D98', '#7E818A', '#60646C', '#1C2024'],
  iris:     ['#FDFDFF', '#F8F8FF', '#F0F4FF', '#E6EDFE', '#D9E2FC', '#C6D4F9', '#AEC0F5', '#8DA4EF', '#5B5BD6', '#5353CE', '#4747C2', '#1F1F7A'],
  indigo:   ['#FDFDFE', '#F7F9FF', '#EDF2FE', '#E1E9FF', '#D2DEFF', '#C1D0FF', '#ABBDF9', '#8DA4EF', '#3E63DD', '#3A5CCC', '#3451B2', '#1F2D5C'],
  jade:     ['#FBFEFD', '#EFFDF6', '#E9F9EE', '#DDF3E4', '#CCEBD7', '#B4DFC4', '#8ECEAA', '#56BA9F', '#29A383', '#26946A', '#208368', '#1D3B31'],
  grass:    ['#FBFEFB', '#F3FCF3', '#EBF9EB', '#DEF3DF', '#CEEBCF', '#B7DFBA', '#97CF9C', '#65BA75', '#46A758', '#3E9B4F', '#2A7E3B', '#203C25'],
  amber:    ['#FEFDFB', '#FEFBE9', '#FFF7C2', '#FFEE9C', '#FBE577', '#F3D673', '#E9C368', '#E2A336', '#FFB224', '#FFA01C', '#AD5700', '#4E2009'],
  tomato:   ['#FFFCFC', '#FFF8F7', '#FEEBE7', '#FFDBD1', '#FFCDBE', '#FDBDAA', '#F5A390', '#EC8E7B', '#E54D2E', '#DC3E1C', '#CA3214', '#341711'],
  crimson:  ['#FFFCFD', '#FEF7F9', '#FFE9F0', '#FEDCE7', '#FACEDA', '#F3BED1', '#EAACC3', '#E093B2', '#E93D82', '#DE3478', '#CB1D63', '#621639'],
  plum:     ['#FEFCFF', '#FDF7FD', '#FBEBFB', '#F7DEF8', '#F2D0F3', '#E9C2EC', '#DEADE3', '#CF91D8', '#AB4ABA', '#A144AF', '#953EA3', '#53195D'],
  cyan:     ['#FAFDFE', '#F2FAFB', '#DEF7F9', '#CAF1F6', '#B5E9F0', '#9DDDE7', '#7DCEDC', '#3DB9CF', '#00A2C7', '#0797B9', '#107D98', '#0D3C48'],
};

// Palette: Slate (neutral) + Iris (primary accent) — but with full semantic spectrum
const P = {
  gray: SCALES.slate,
  primary: SCALES.iris,
  iris: SCALES.iris,
  slate: SCALES.slate,
  indigo: SCALES.indigo,
  jade: SCALES.jade,
  grass: SCALES.grass,
  amber: SCALES.amber,
  tomato: SCALES.tomato,
  crimson: SCALES.crimson,
  plum: SCALES.plum,
  cyan: SCALES.cyan,
};

const T = {
  bg: P.gray[0],
  subtle: P.gray[1],
  surface: '#FFFFFF',
  border: P.gray[3],
  borderStrong: P.gray[5],
  divider: P.gray[2],
  inkLow: P.gray[8],
  inkMuted: P.gray[10],
  ink: P.gray[11],
  inkStrong: P.gray[11],
};

const FONT_LINK = (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
    .font-geist { font-family: 'Geist', system-ui, -apple-system, sans-serif; }
    .font-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; }
  `}</style>
);

// ===== Atoms =====
const Avatar = ({ name, size = 'md', hue = 'slate' }) => {
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();
  const sizes = { sm: 'w-7 h-7 text-[10px]', md: 'w-9 h-9 text-xs', lg: 'w-11 h-11 text-sm' };
  const hues = {
    slate: { bg: P.gray[3], fg: P.gray[11] },
    iris: { bg: P.primary[3], fg: P.primary[11] },
    jade: { bg: P.jade[3], fg: P.jade[11] },
    crimson: { bg: P.crimson[3], fg: P.crimson[11] },
    indigo: { bg: P.indigo[3], fg: P.indigo[11] },
    amber: { bg: P.amber[3], fg: P.amber[11] },
  };
  const h = hues[hue] || hues.slate;
  return (
    <div className={`${sizes[size]} rounded-full flex items-center justify-center font-semibold flex-shrink-0`}
      style={{ background: h.bg, color: h.fg }}>
      {initials}
    </div>
  );
};

const Pill = ({ color, children, variant = 'soft', icon: Icon }) => {
  const scale = P[color];
  const styles = variant === 'soft'
    ? { background: scale[2], color: scale[11], border: `1px solid ${scale[4]}` }
    : { background: scale[9], color: '#FFF' };
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-tight"
      style={styles}>
      {Icon && <Icon className="w-2.5 h-2.5" strokeWidth={2.2} />}
      {children}
    </span>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    confirmada: { c: 'jade', l: 'Confirmada' },
    agendada:   { c: 'amber', l: 'Não confirmada' },
    realizada:  { c: 'slate', l: 'Realizada' },
    cancelada:  { c: 'tomato', l: 'Cancelada' },
    falta:      { c: 'tomato', l: 'Falta' },
  }[status];
  const scale = map.c === 'slate' ? P.gray : P[map.c];
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold"
      style={{ background: scale[2], color: scale[11], border: `1px solid ${scale[4]}` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: scale[9] }} />
      {map.l}
    </span>
  );
};

const TipoTag = ({ tipo }) => {
  const map = {
    presencial: { icon: MapPin,   label: 'Presencial', c: 'indigo' },
    online:     { icon: Video,    label: 'Online',     c: 'cyan'   },
    retorno:    { icon: RotateCcw,label: 'Retorno',    c: 'crimson'},
  }[tipo];
  const scale = P[map.c];
  const Icon = map.icon;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider"
      style={{ background: scale[3], color: scale[11] }}>
      <Icon className="w-2.5 h-2.5" strokeWidth={2.2} />
      {map.label}
    </span>
  );
};

const PagamentoBadge = ({ estado, valor }) => {
  const cfg = {
    zero:    { scale: P.tomato },
    parcial: { scale: P.amber },
    pago:    { scale: P.jade },
  }[estado];
  return (
    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-mono font-semibold tabular-nums"
      style={{ background: cfg.scale[3], color: cfg.scale[11] }}>
      R$ {valor}
    </span>
  );
};

// ===== Chrome =====
const Sidebar = ({ active }) => {
  const items = [
    { key: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { key: 'agenda', icon: Calendar, label: 'Agenda' },
    { key: 'pacientes', icon: Users, label: 'Pacientes' },
    { key: 'retornos', icon: RotateCcw, label: 'Retornos', badge: '12' },
    { key: 'prontuarios', icon: FileText, label: 'Prontuários' },
    { key: 'configuracoes', icon: Settings, label: 'Configurações' },
  ];
  return (
    <aside className="w-60 flex flex-col flex-shrink-0 border-r" style={{ background: T.surface, borderColor: T.border }}>
      <div className="p-5 border-b flex items-center gap-2.5" style={{ borderColor: T.border }}>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: `linear-gradient(135deg, ${P.primary[9]}, ${P.primary[11]})` }}>
          <Stethoscope className="w-4 h-4" strokeWidth={2} style={{ color: '#FFF' }} />
        </div>
        <div>
          <div className="text-[14px] font-semibold leading-none tracking-tight" style={{ color: T.ink }}>Agenda Mais</div>
          <div className="text-[9px] tracking-[0.18em] uppercase mt-1" style={{ color: T.inkLow }}>Med</div>
        </div>
      </div>
      <nav className="flex-1 p-2 space-y-0.5">
        {items.map(it => {
          const Icon = it.icon;
          const isActive = it.key === active;
          return (
            <a key={it.key} href="#"
              className="flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium transition-colors"
              style={isActive
                ? { background: P.primary[3], color: P.primary[11] }
                : { color: T.ink }}>
              <Icon className="w-4 h-4" strokeWidth={1.8}
                style={{ color: isActive ? P.primary[11] : T.inkMuted }} />
              <span className="flex-1">{it.label}</span>
              {it.badge && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                  style={{ background: isActive ? P.primary[9] : P.amber[3], color: isActive ? '#FFF' : P.amber[11] }}>
                  {it.badge}
                </span>
              )}
            </a>
          );
        })}
      </nav>
      <div className="p-2 border-t" style={{ borderColor: T.border }}>
        <div className="flex items-center gap-2 p-1.5">
          <Avatar name="Nathalie Sanches" size="sm" hue="iris" />
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-semibold truncate" style={{ color: T.ink }}>Dra. Nathalie</div>
            <div className="text-[10px] truncate" style={{ color: T.inkLow }}>Ginecologia</div>
          </div>
        </div>
      </div>
    </aside>
  );
};

const TopBar = ({ title, subtitle, children }) => (
  <header className="border-b px-7 py-4 flex items-center justify-between flex-shrink-0"
    style={{ background: T.surface, borderColor: T.border }}>
    <div>
      <h1 className="text-xl font-semibold tracking-tight" style={{ color: T.inkStrong }}>{title}</h1>
      {subtitle && <p className="text-[12px] mt-0.5" style={{ color: T.inkLow }}>{subtitle}</p>}
    </div>
    <div className="flex items-center gap-2">
      {children}
      <div className="relative">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.8} style={{ color: T.inkLow }} />
        <input type="text" placeholder="Buscar…"
          className="pl-9 pr-3 py-1.5 text-[12px] rounded-md w-56 focus:outline-none placeholder:opacity-60"
          style={{ background: T.subtle, border: `1px solid ${T.border}`, color: T.ink }}/>
      </div>
      <button className="w-8 h-8 rounded-md flex items-center justify-center relative" style={{ color: T.inkMuted }}>
        <Bell className="w-4 h-4" strokeWidth={1.8} />
        <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full" style={{ background: P.tomato[9] }}/>
      </button>
    </div>
  </header>
);

// ===== KPI card with tinted accent =====
const KPI = ({ label, value, delta, hue = 'slate', icon: Icon, up = true }) => {
  const scale = hue === 'slate' ? P.gray : P[hue];
  return (
    <div className="rounded-xl border p-4 relative overflow-hidden" style={{ background: T.surface, borderColor: T.border }}>
      {hue !== 'slate' && (
        <div className="absolute top-0 right-0 w-24 h-24 rounded-full -mr-10 -mt-10 opacity-40"
          style={{ background: `radial-gradient(circle, ${scale[3]}, transparent 70%)` }}/>
      )}
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.1em] font-semibold" style={{ color: T.inkLow }}>{label}</div>
          {Icon && (
            <div className="w-7 h-7 rounded-lg flex items-center justify-center"
              style={{ background: scale[3], color: scale[11] }}>
              <Icon className="w-3.5 h-3.5" strokeWidth={2} />
            </div>
          )}
        </div>
        <div className="font-mono text-[24px] font-semibold tabular-nums tracking-tight leading-none"
          style={{ color: hue === 'slate' ? T.ink : scale[11] }}>{value}</div>
        {delta && (
          <div className="flex items-center gap-1 mt-2 text-[11px]" style={{ color: up ? P.jade[11] : P.tomato[11] }}>
            {up ? '↗' : '↘'} {delta}
          </div>
        )}
      </div>
    </div>
  );
};

// ======================= AGENDA =======================
const AgendaScreen = () => {
  const consultas = [
    { hora: '09:00', dur: '50min', nome: 'Beatriz Mendes Oliveira', tipo: 'presencial', status: 'confirmada', proc: 'Consulta Ginecológica', valor: '320', pg: 'pago', avatarHue: 'iris' },
    { hora: '10:30', dur: '40min', nome: 'Carla Souza Lima', tipo: 'online', status: 'agendada', proc: 'Reposição Hormonal', valor: '0', pg: 'zero', avatarHue: 'slate' },
    { hora: '11:30', dur: '45min', nome: 'Juliana Alves', tipo: 'presencial', status: 'confirmada', proc: 'Consulta Ginecológica', valor: '180', pg: 'parcial', avatarHue: 'amber' },
    { hora: '14:00', dur: '30min', nome: 'Helena Martins Barbosa', tipo: 'retorno', status: 'confirmada', proc: 'Retorno', valor: null, avatarHue: 'crimson' },
    { hora: '15:30', dur: '60min', nome: 'Mariana Dias Cardoso', tipo: 'presencial', status: 'realizada', proc: 'Preventivo · Papanicolau', valor: '250', pg: 'pago', avatarHue: 'jade' },
    { hora: '17:00', dur: '40min', nome: 'Paula Ribeiro Santos', tipo: 'online', status: 'agendada', proc: 'Consulta Ginecológica', valor: '150', pg: 'parcial', avatarHue: 'indigo' },
  ];

  return (
    <div className="flex h-full font-geist" style={{ background: T.bg }}>
      <Sidebar active="agenda" />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar title="Agenda" subtitle="Quinta · 30 de abril, 2026">
          <button className="px-3 py-1.5 rounded-md text-[12px] font-semibold flex items-center gap-1.5 shadow-sm"
            style={{ background: P.primary[9], color: '#FFF' }}>
            <Plus className="w-3.5 h-3.5" strokeWidth={2.2}/>
            Nova Consulta
          </button>
        </TopBar>

        <main className="flex-1 overflow-y-auto p-6">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <button className="px-3 py-1.5 text-[12px] font-medium rounded-md border"
                style={{ borderColor: T.border, color: T.ink, background: T.surface }}>Hoje</button>
              <div className="flex items-center rounded-md border overflow-hidden" style={{ borderColor: T.border, background: T.surface }}>
                <button className="p-1.5" style={{ color: T.inkMuted }}><ChevronLeft className="w-4 h-4" strokeWidth={1.8}/></button>
                <div className="w-px h-5" style={{ background: T.border }}/>
                <button className="p-1.5" style={{ color: T.inkMuted }}><ChevronRight className="w-4 h-4" strokeWidth={1.8}/></button>
              </div>
              <h2 className="text-[14px] font-semibold ml-2 tracking-tight" style={{ color: T.ink }}>30 abril → 02 maio 2026</h2>
            </div>
            <div className="inline-flex p-0.5 rounded-md border" style={{ background: T.subtle, borderColor: T.border }}>
              <button className="px-3 py-1 text-[11px] font-semibold rounded" style={{ background: T.surface, color: T.ink, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>Dia</button>
              <button className="px-3 py-1 text-[11px] font-medium rounded" style={{ color: T.inkMuted }}>Semana</button>
              <button className="px-3 py-1 text-[11px] font-medium rounded" style={{ color: T.inkMuted }}>Mês</button>
            </div>
          </div>

          {/* KPIs colorful */}
          <div className="grid grid-cols-4 gap-3 mb-5">
            <KPI label="Consultas hoje" value="6" delta="2 confirmadas" hue="iris" icon={Calendar} />
            <KPI label="Recebido hoje" value="R$ 570" delta="+12%" hue="jade" icon={DollarSign} />
            <KPI label="Pendente" value="R$ 330" delta="2 consultas" hue="amber" icon={Clock} up={false}/>
            <KPI label="Retornos a agendar" value="12" delta="3 fora do prazo" hue="crimson" icon={RotateCcw} up={false}/>
          </div>

          {/* List */}
          <div className="rounded-xl border overflow-hidden shadow-sm" style={{ background: T.surface, borderColor: T.border }}>
            <div className="px-5 py-3 border-b flex items-center justify-between"
              style={{ borderColor: T.border, background: `linear-gradient(180deg, ${P.primary[2]}, transparent)` }}>
              <div className="flex items-center gap-2">
                <div className="w-1 h-4 rounded-full" style={{ background: P.primary[9] }}/>
                <span className="text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: T.ink }}>Quinta · 30 abril</span>
                <span className="text-[11px]" style={{ color: T.inkLow }}>· 6 consultas</span>
              </div>
              <button className="text-[11px] font-medium hover:underline" style={{ color: P.primary[11] }}>Expandir →</button>
            </div>

            <div>
              {consultas.map((c, i) => (
                <div key={i} className="flex items-stretch border-b last:border-b-0 transition-colors hover:bg-black/[0.015] cursor-pointer"
                  style={{ borderColor: T.divider }}>
                  <div className="px-5 py-3.5 flex flex-col justify-center border-r" style={{ borderColor: T.divider, width: 100 }}>
                    <div className="font-mono text-[14px] font-semibold tabular-nums" style={{ color: T.ink }}>{c.hora}</div>
                    <div className="font-mono text-[10px] mt-0.5 tabular-nums" style={{ color: T.inkLow }}>{c.dur}</div>
                  </div>

                  <div className="flex-1 px-5 py-3.5 flex items-center gap-4">
                    <Avatar name={c.nome} hue={c.avatarHue} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[13px] font-semibold truncate" style={{ color: T.ink }}>{c.nome}</span>
                        <TipoTag tipo={c.tipo} />
                      </div>
                      <div className="text-[11px]" style={{ color: T.inkLow }}>{c.proc}</div>
                    </div>

                    <StatusPill status={c.status} />

                    <div className="w-20 text-right">
                      {c.valor !== null && <PagamentoBadge estado={c.pg} valor={c.valor}/>}
                    </div>

                    <button className="w-7 h-7 rounded-md flex items-center justify-center hover:bg-black/[0.04]" style={{ color: T.inkMuted }}>
                      <MoreHorizontal className="w-4 h-4" strokeWidth={1.8}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-5 py-3 flex items-center justify-between text-[11px]"
              style={{ color: T.inkLow, background: T.subtle, borderTop: `1px solid ${T.border}` }}>
              <span>6 consultas · <span className="font-semibold" style={{ color: P.jade[11] }}>R$ 570</span> recebido · <span className="font-semibold" style={{ color: P.amber[11] }}>R$ 330</span> pendente</span>
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full" style={{ background: P.jade[9] }}/> Pago</span>
                <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full" style={{ background: P.amber[9] }}/> Parcial</span>
                <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full" style={{ background: P.tomato[9] }}/> Pendente</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// ======================= CONTROLE DE RETORNO =======================
const ControleRetornoScreen = () => {
  const pacientes = [
    { nome: 'Aline Costa Mendes', ultima: '30/03/2026', dias: 12, prazo: 'dentro', consulta: 'Consulta Ginecológica', agendado: true, dataAgendada: '15/05/2026' },
    { nome: 'Sandra Silva Martins', ultima: '23/03/2026', dias: 19, prazo: 'dentro', consulta: 'Reposição Hormonal', agendado: false, dataAgendada: null },
    { nome: 'Júlia Barbosa Santos', ultima: '16/02/2026', dias: 55, prazo: 'fora', consulta: 'Preventivo', agendado: false, dataAgendada: null },
    { nome: 'Marta Oliveira Cruz', ultima: '10/03/2026', dias: 32, prazo: 'personalizado', consulta: 'Consulta Ginecológica', agendado: true, dataAgendada: '20/05/2026' },
    { nome: 'Lúcia Fernandes Rocha', ultima: '28/01/2026', dias: 72, prazo: 'fora', consulta: 'Consulta Ginecológica', agendado: false, dataAgendada: null },
    { nome: 'Renata Souza Costa', ultima: '05/04/2026', dias: 6, prazo: 'dentro', consulta: 'Preventivo', agendado: true, dataAgendada: '12/05/2026' },
  ];

  return (
    <div className="flex h-full font-geist" style={{ background: T.bg }}>
      <Sidebar active="retornos" />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar title="Controle de Retorno" subtitle="Gestão inteligente de retornos de pacientes">
          <button className="px-3 py-1.5 rounded-md text-[12px] font-medium border flex items-center gap-1.5"
            style={{ background: T.surface, borderColor: T.border, color: T.ink }}>
            <Settings className="w-3.5 h-3.5" strokeWidth={1.8}/>
            Configurar padrões
          </button>
        </TopBar>

        <main className="flex-1 overflow-y-auto p-6">
          {/* 4 status cards */}
          <div className="grid grid-cols-4 gap-3 mb-5">
            {[
              { l: 'Disponíveis', v: '24', s: 'pacientes com retorno pendente', hue: 'iris', icon: Users, active: true },
              { l: 'Agendados', v: '12', s: 'com data marcada', hue: 'jade', icon: CheckCircle2 },
              { l: 'Não agendados', v: '9', s: 'precisam de contato', hue: 'amber', icon: Clock },
              { l: 'Fora do prazo', v: '3', s: 'alerta — prazo excedido', hue: 'tomato', icon: AlertCircle },
            ].map((card, i) => {
              const scale = P[card.hue];
              const Icon = card.icon;
              return (
                <button key={i} className="rounded-xl border p-4 text-left transition-all relative overflow-hidden"
                  style={card.active
                    ? { background: T.surface, borderColor: scale[9], boxShadow: `0 0 0 3px ${scale[4]}` }
                    : { background: T.surface, borderColor: T.border }}>
                  <div className="absolute top-0 right-0 w-24 h-24 rounded-full -mr-10 -mt-10 opacity-50"
                    style={{ background: `radial-gradient(circle, ${scale[3]}, transparent 70%)` }}/>
                  <div className="relative">
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-[10px] uppercase tracking-[0.1em] font-semibold" style={{ color: T.inkLow }}>{card.l}</div>
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                        style={{ background: scale[3], color: scale[11] }}>
                        <Icon className="w-3.5 h-3.5" strokeWidth={2} />
                      </div>
                    </div>
                    <div className="font-mono text-[28px] font-semibold tabular-nums tracking-tight leading-none"
                      style={{ color: scale[11] }}>{card.v}</div>
                    <div className="text-[11px] mt-1.5" style={{ color: T.inkLow }}>{card.s}</div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Filter chips */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: T.inkLow }}>
              <Filter className="w-3.5 h-3.5" strokeWidth={1.8}/>
              Filtrar:
            </div>
            {[
              { k: 'todos', l: 'Todos', active: true, count: 24 },
              { k: 'agendados', l: 'Agendados', count: 12, color: 'jade' },
              { k: 'nao_agendados', l: 'Não Agendados', count: 9, color: 'amber' },
              { k: 'fora_prazo', l: 'Fora do Prazo', count: 3, color: 'tomato' },
              { k: 'personalizado', l: 'Personalizado', count: 2, color: 'plum' },
            ].map(f => {
              const scale = f.color ? P[f.color] : P.primary;
              return (
                <button key={f.k}
                  className="px-3 py-1.5 rounded-full text-[11px] font-semibold flex items-center gap-1.5 border transition-all"
                  style={f.active
                    ? { background: P.primary[9], color: '#FFF', borderColor: P.primary[9] }
                    : { background: T.surface, color: T.ink, borderColor: T.border }}>
                  {f.l}
                  <span className="px-1.5 py-0 rounded text-[10px] font-mono"
                    style={f.active
                      ? { background: 'rgba(255,255,255,0.25)', color: '#FFF' }
                      : { background: scale[3], color: scale[11] }}>{f.count}</span>
                </button>
              );
            })}
          </div>

          {/* Table */}
          <div className="rounded-xl border overflow-hidden shadow-sm" style={{ background: T.surface, borderColor: T.border }}>
            <div className="grid grid-cols-[1fr_140px_180px_130px_120px] px-5 py-2.5 border-b text-[10px] uppercase tracking-[0.1em] font-bold"
              style={{ borderColor: T.border, color: T.inkLow, background: T.subtle }}>
              <div>Paciente</div>
              <div>Última consulta</div>
              <div>Status</div>
              <div>Retorno agendado</div>
              <div className="text-right">Ações</div>
            </div>
            {pacientes.map((p, i) => (
              <div key={i} className="grid grid-cols-[1fr_140px_180px_130px_120px] items-center px-5 py-3 border-b last:border-b-0 hover:bg-black/[0.015] transition-colors"
                style={{ borderColor: T.divider }}>
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar name={p.nome} size="sm" hue={p.prazo === 'fora' ? 'crimson' : p.prazo === 'personalizado' ? 'amber' : 'iris'}/>
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold truncate" style={{ color: T.ink }}>{p.nome}</div>
                    <div className="text-[11px]" style={{ color: T.inkLow }}>{p.consulta}</div>
                  </div>
                </div>
                <div>
                  <div className="font-mono text-[11px] tabular-nums" style={{ color: T.ink }}>{p.ultima}</div>
                  <div className="text-[10px]" style={{ color: T.inkLow }}>há {p.dias} dias</div>
                </div>
                <div>
                  {p.prazo === 'fora' && <Pill color="tomato" icon={AlertTriangle}>Fora do prazo</Pill>}
                  {p.prazo === 'personalizado' && <Pill color="plum">Personalizado</Pill>}
                  {p.prazo === 'dentro' && !p.agendado && <Pill color="amber">Não agendado</Pill>}
                  {p.prazo === 'dentro' && p.agendado && <Pill color="jade" icon={CheckCircle2}>Dentro do prazo</Pill>}
                </div>
                <div className="font-mono text-[11px] tabular-nums" style={{ color: p.agendado ? T.ink : T.inkLow }}>
                  {p.dataAgendada || '—'}
                </div>
                <div className="flex items-center gap-1 justify-end">
                  <button className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: P.jade[3], color: P.jade[11] }} title="WhatsApp">
                    <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.8}/>
                  </button>
                  <button className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: P.primary[3], color: P.primary[11] }} title="Agendar">
                    <Calendar className="w-3.5 h-3.5" strokeWidth={1.8}/>
                  </button>
                  <button className="w-7 h-7 rounded-md flex items-center justify-center" style={{ color: T.inkMuted }}>
                    <MoreHorizontal className="w-3.5 h-3.5" strokeWidth={1.8}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

// ======================= MODAL NOVO LANÇAMENTO =======================
const NovoLancamentoModal = () => {
  const [forma, setForma] = useState('pix');

  return (
    <div className="flex h-full font-geist items-center justify-center p-6 relative" style={{ background: T.bg }}>
      {/* Backdrop context */}
      <div className="absolute inset-0 flex flex-col font-geist" style={{ background: T.bg, opacity: 0.55 }}>
        <Sidebar active="agenda" />
      </div>

      <div className="absolute inset-0" style={{ background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(4px)' }}/>

      {/* Modal */}
      <div className="relative w-[520px] rounded-2xl border shadow-2xl overflow-hidden"
        style={{ background: T.surface, borderColor: T.border }}>
        {/* Header */}
        <div className="px-6 py-5 border-b relative overflow-hidden" style={{ borderColor: T.border }}>
          <div className="absolute top-0 right-0 w-48 h-48 rounded-full -mr-20 -mt-20 opacity-50"
            style={{ background: `radial-gradient(circle, ${P.primary[3]}, transparent 70%)` }}/>
          <div className="relative flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${P.primary[9]}, ${P.primary[11]})` }}>
                <DollarSign className="w-5 h-5" strokeWidth={2} style={{ color: '#FFF' }}/>
              </div>
              <div>
                <h2 className="text-[17px] font-semibold tracking-tight" style={{ color: T.inkStrong }}>Novo Lançamento</h2>
                <p className="text-[12px] mt-0.5" style={{ color: T.inkLow }}>
                  Beatriz Mendes · Consulta Ginecológica · 30/04 09:00
                </p>
              </div>
            </div>
            <button className="w-8 h-8 rounded-md flex items-center justify-center hover:bg-black/[0.04]" style={{ color: T.inkMuted }}>
              <X className="w-4 h-4" strokeWidth={1.8}/>
            </button>
          </div>
        </div>

        {/* Summary strip */}
        <div className="px-6 py-3 grid grid-cols-3 gap-3 border-b" style={{ borderColor: T.border, background: T.subtle }}>
          {[
            { l: 'Valor total', v: 'R$ 320,00', c: T.ink },
            { l: 'Já recebido', v: 'R$ 200,00', c: P.jade[11] },
            { l: 'Restante', v: 'R$ 120,00', c: P.amber[11] },
          ].map((x, i) => (
            <div key={i}>
              <div className="text-[9px] uppercase tracking-[0.1em] font-bold" style={{ color: T.inkLow }}>{x.l}</div>
              <div className="font-mono text-[14px] font-semibold tabular-nums mt-1" style={{ color: x.c }}>{x.v}</div>
            </div>
          ))}
        </div>

        {/* Form */}
        <div className="p-6 space-y-5">
          {/* Valor */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.1em] font-bold mb-2 block" style={{ color: T.inkLow }}>
              Valor do lançamento
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 font-mono text-[16px] font-semibold" style={{ color: T.inkMuted }}>R$</span>
              <input type="text" defaultValue="120,00"
                className="w-full pl-12 pr-3 py-3 rounded-lg border font-mono text-[20px] font-semibold tabular-nums focus:outline-none"
                style={{ borderColor: P.primary[6], background: P.primary[1], color: T.inkStrong, boxShadow: `0 0 0 2px ${P.primary[3]}` }}/>
            </div>
            <div className="flex gap-1.5 mt-2">
              {[
                { l: 'R$ 60,00', s: '50%' },
                { l: 'R$ 120,00', s: 'Quitação', active: true },
                { l: 'Personalizado', s: null },
              ].map((q, i) => (
                <button key={i} className="flex-1 px-2 py-1.5 rounded-md text-[11px] font-medium border"
                  style={q.active
                    ? { background: P.primary[3], color: P.primary[11], borderColor: P.primary[6] }
                    : { background: T.surface, color: T.inkMuted, borderColor: T.border }}>
                  <div className="font-mono font-semibold">{q.l}</div>
                  {q.s && <div className="text-[9px] font-normal mt-0.5 opacity-80">{q.s}</div>}
                </button>
              ))}
            </div>
          </div>

          {/* Forma de pagamento */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.1em] font-bold mb-2 block" style={{ color: T.inkLow }}>
              Forma de pagamento
            </label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { k: 'pix', l: 'PIX', icon: QrCode, color: 'jade' },
                { k: 'cartao', l: 'Cartão', icon: CreditCard, color: 'indigo' },
                { k: 'dinheiro', l: 'Dinheiro', icon: Banknote, color: 'grass' },
                { k: 'transf', l: 'Transfer.', icon: Smartphone, color: 'cyan' },
              ].map(f => {
                const active = forma === f.k;
                const scale = P[f.color];
                const Icon = f.icon;
                return (
                  <button key={f.k} onClick={() => setForma(f.k)}
                    className="py-3 rounded-lg border flex flex-col items-center gap-1.5 transition-all"
                    style={active
                      ? { background: scale[3], borderColor: scale[9], color: scale[11], boxShadow: `0 0 0 2px ${scale[4]}` }
                      : { background: T.surface, borderColor: T.border, color: T.inkMuted }}>
                    <Icon className="w-4 h-4" strokeWidth={1.8} />
                    <span className="text-[11px] font-semibold">{f.l}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Observação */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.1em] font-bold mb-2 block" style={{ color: T.inkLow }}>
              Observação <span className="font-normal normal-case" style={{ color: T.inkLow }}>(opcional)</span>
            </label>
            <textarea rows={2} placeholder="Ex: Quitação via PIX — comprovante no WhatsApp"
              className="w-full px-3 py-2.5 rounded-lg border text-[12px] focus:outline-none placeholder:opacity-60 resize-none"
              style={{ borderColor: T.border, background: T.surface, color: T.ink }}/>
          </div>

          {/* Ajustar valor total */}
          <div className="rounded-lg border p-3.5 flex items-center gap-3"
            style={{ borderColor: P.amber[4], background: P.amber[2] }}>
            <AlertTriangle className="w-4 h-4 flex-shrink-0" strokeWidth={2} style={{ color: P.amber[11] }}/>
            <div className="flex-1 text-[11px]" style={{ color: P.amber[11] }}>
              Após este lançamento, o total recebido será <span className="font-mono font-semibold">R$ 320,00</span> — consulta <span className="font-semibold">quitada</span>.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-between"
          style={{ borderColor: T.border, background: T.subtle }}>
          <button className="text-[12px] font-medium" style={{ color: T.inkMuted }}>Cancelar</button>
          <div className="flex items-center gap-2">
            <button className="px-4 py-2 rounded-md text-[12px] font-medium border"
              style={{ background: T.surface, borderColor: T.border, color: T.ink }}>
              Salvar e fechar
            </button>
            <button className="px-5 py-2 rounded-md text-[12px] font-semibold flex items-center gap-1.5 shadow-sm"
              style={{ background: P.primary[9], color: '#FFF' }}>
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.2}/>
              Registrar pagamento
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ======================= AGENDA SEMANA =======================
// Layout: 1 dia expandido (full cards) + 6 dias miniCompact, click-to-expand
const AgendaSemanaScreen = () => {
  const [expandedDay, setExpandedDay] = useState(2); // QUI = 2

  const days = [
    { label: 'SEG', date: '28', count: 6 },
    { label: 'TER', date: '29', count: 8 },
    { label: 'QUA', date: '30', count: 5, isToday: true },
    { label: 'QUI', date: '01', count: 7 },
    { label: 'SEX', date: '02', count: 4 },
    { label: 'SÁB', date: '03', count: 2 },
    { label: 'DOM', date: '04', count: 0 },
  ];

  const hours = ['08:00', '09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00'];

  // Events per day (mock)
  const eventsByDay = {
    0: [{ h: 1, dur: 1, nome: 'Mariana T.', tipo: 'presencial', status: 'realizada', pg: 'pago' }, { h: 5, dur: 1, nome: 'Bruna L.', tipo: 'online', status: 'realizada', pg: 'pago' }, { h: 8, dur: 1, nome: 'Larissa M.', tipo: 'retorno', status: 'realizada', pg: null }],
    1: [{ h: 0, dur: 1, nome: 'Camila P.', tipo: 'presencial', status: 'realizada', pg: 'pago' }, { h: 2, dur: 1, nome: 'Renata C.', tipo: 'online', status: 'realizada', pg: 'pago' }, { h: 4, dur: 1, nome: 'Patrícia S.', tipo: 'presencial', status: 'realizada', pg: 'parcial' }, { h: 6, dur: 2, nome: 'Sandra L.', tipo: 'presencial', status: 'realizada', pg: 'pago' }, { h: 9, dur: 1, nome: 'Ana B.', tipo: 'retorno', status: 'realizada', pg: null }],
    2: [
      { h: 1, dur: 1, hora: '09:00', nome: 'Beatriz Mendes Oliveira', tipo: 'presencial', status: 'confirmada', proc: 'Consulta Ginecológica', valor: '320', pg: 'pago' },
      { h: 2, dur: 1, hora: '10:30', nome: 'Carla Souza Lima', tipo: 'online', status: 'agendada', proc: 'Reposição Hormonal', valor: '0', pg: 'zero' },
      { h: 6, dur: 1, hora: '14:00', nome: 'Helena Martins Barbosa', tipo: 'retorno', status: 'confirmada', proc: 'Retorno', valor: null, pg: null },
      { h: 7, dur: 2, hora: '15:30', nome: 'Mariana Dias Cardoso', tipo: 'presencial', status: 'realizada', proc: 'Preventivo', valor: '250', pg: 'pago' },
      { h: 9, dur: 1, hora: '17:00', nome: 'Paula Ribeiro Santos', tipo: 'online', status: 'agendada', proc: 'Consulta', valor: '150', pg: 'parcial' },
    ],
    3: [{ h: 0, dur: 1, nome: 'Júlia A.', tipo: 'presencial', status: 'agendada', pg: 'zero' }, { h: 1, dur: 1, nome: 'Aline R.', tipo: 'online', status: 'confirmada', pg: 'pago' }, { h: 3, dur: 1, nome: 'Sofia C.', tipo: 'retorno', status: 'agendada', pg: null }, { h: 5, dur: 1, nome: 'Lucia P.', tipo: 'presencial', status: 'agendada', pg: 'parcial' }, { h: 7, dur: 1, nome: 'Marta D.', tipo: 'presencial', status: 'agendada', pg: 'zero' }, { h: 8, dur: 1, nome: 'Rafa B.', tipo: 'online', status: 'agendada', pg: 'zero' }, { h: 9, dur: 1, nome: 'Vera M.', tipo: 'presencial', status: 'agendada', pg: 'pago' }],
    4: [{ h: 1, dur: 1, nome: 'Cristina M.', tipo: 'presencial', status: 'agendada', pg: 'zero' }, { h: 4, dur: 1, nome: 'Ester S.', tipo: 'online', status: 'agendada', pg: 'parcial' }, { h: 7, dur: 1, nome: 'Tania F.', tipo: 'presencial', status: 'agendada', pg: 'zero' }, { h: 9, dur: 1, nome: 'Iara N.', tipo: 'retorno', status: 'agendada', pg: null }],
    5: [{ h: 1, dur: 1, nome: 'Bia O.', tipo: 'presencial', status: 'agendada', pg: 'zero' }, { h: 3, dur: 1, nome: 'Clara V.', tipo: 'retorno', status: 'agendada', pg: null }],
    6: [],
  };

  const STATUS_BG = {
    confirmada: { bg: P.jade[2], stripe: P.jade[9], txt: P.jade[11] },
    agendada:   { bg: P.amber[2], stripe: P.amber[9], txt: P.amber[11] },
    realizada:  { bg: P.gray[2], stripe: P.gray[9], txt: P.gray[11] },
    cancelada:  { bg: P.tomato[2], stripe: P.tomato[9], txt: P.tomato[11] },
  };

  const TIPO_DOT = {
    presencial: P.indigo[9],
    online: P.cyan[9],
    retorno: P.crimson[9],
  };

  const HOUR_HEIGHT = 52; // px per hour
  const expandedFlex = '4 1 0%';
  const miniFlex = '1 1 0%';

  return (
    <div className="flex h-full font-geist" style={{ background: T.bg }}>
      <Sidebar active="agenda" />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar title="Agenda" subtitle="Semana de 28 abril → 04 maio, 2026">
          <button className="px-3 py-1.5 rounded-md text-[12px] font-semibold flex items-center gap-1.5 shadow-sm"
            style={{ background: P.primary[9], color: '#FFF' }}>
            <Plus className="w-3.5 h-3.5" strokeWidth={2.2}/>
            Nova Consulta
          </button>
        </TopBar>

        <main className="flex-1 overflow-hidden p-4 flex flex-col">
          {/* Toolbar */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <button className="px-3 py-1.5 text-[12px] font-medium rounded-md border"
                style={{ borderColor: T.border, color: T.ink, background: T.surface }}>Hoje</button>
              <div className="flex items-center rounded-md border overflow-hidden" style={{ borderColor: T.border, background: T.surface }}>
                <button className="p-1.5" style={{ color: T.inkMuted }}><ChevronLeft className="w-4 h-4" strokeWidth={1.8}/></button>
                <div className="w-px h-5" style={{ background: T.border }}/>
                <button className="p-1.5" style={{ color: T.inkMuted }}><ChevronRight className="w-4 h-4" strokeWidth={1.8}/></button>
              </div>
              <h2 className="text-[14px] font-semibold ml-2 tracking-tight" style={{ color: T.ink }}>28 abril → 04 maio 2026</h2>
              <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full"
                style={{ background: P.primary[3], color: P.primary[11] }}>32 consultas · R$ 6.840 previstos</span>
            </div>
            <div className="inline-flex p-0.5 rounded-md border" style={{ background: T.subtle, borderColor: T.border }}>
              <button className="px-3 py-1 text-[11px] font-medium rounded" style={{ color: T.inkMuted }}>Dia</button>
              <button className="px-3 py-1 text-[11px] font-semibold rounded" style={{ background: T.surface, color: T.ink, boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>Semana</button>
              <button className="px-3 py-1 text-[11px] font-medium rounded" style={{ color: T.inkMuted }}>Mês</button>
            </div>
          </div>

          {/* Calendar grid */}
          <div className="flex-1 rounded-xl border overflow-hidden flex flex-col" style={{ background: T.surface, borderColor: T.border }}>
            {/* Day headers */}
            <div className="flex border-b" style={{ borderColor: T.border, background: T.subtle }}>
              <div style={{ width: 48 }} className="border-r flex-shrink-0" />
              {days.map((d, i) => {
                const isExpanded = i === expandedDay;
                return (
                  <button key={i} onClick={() => setExpandedDay(i)}
                    className="border-r last:border-r-0 px-3 py-2.5 transition-all relative text-left min-w-0"
                    style={{
                      flex: isExpanded ? expandedFlex : miniFlex,
                      borderColor: T.border,
                      background: isExpanded ? T.surface : 'transparent',
                      boxShadow: isExpanded ? `inset 0 -2px 0 ${P.primary[9]}` : 'none',
                    }}>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-[9px] uppercase tracking-[0.15em] font-bold"
                        style={{ color: d.isToday ? P.primary[11] : T.inkLow }}>{d.label}</span>
                      <span className="font-mono text-[16px] font-semibold tabular-nums leading-none"
                        style={{ color: isExpanded ? P.primary[11] : (d.isToday ? P.primary[11] : T.ink) }}>
                        {d.date}
                      </span>
                      {d.isToday && (
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: P.primary[9] }}/>
                      )}
                    </div>
                    <div className="text-[10px] mt-1" style={{ color: T.inkLow }}>
                      {d.count > 0 ? `${d.count} consultas` : 'livre'}
                    </div>
                    {isExpanded && (
                      <div className="absolute top-2 right-2 px-1.5 py-0.5 rounded text-[8px] font-bold uppercase tracking-wider"
                        style={{ background: P.primary[9], color: '#FFF' }}>Foco</div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Body: time gutter + day columns */}
            <div className="flex-1 flex overflow-y-auto">
              {/* Time gutter */}
              <div style={{ width: 48 }} className="flex-shrink-0 border-r relative">
                <div className="border-r h-full" style={{ borderColor: T.border }}>
                  {hours.map((h, i) => (
                    <div key={i} className="relative" style={{ height: HOUR_HEIGHT }}>
                      <span className="absolute -top-1.5 right-2 font-mono text-[9px] tabular-nums"
                        style={{ color: T.inkLow }}>{h}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Day columns */}
              {days.map((d, dayIdx) => {
                const isExpanded = dayIdx === expandedDay;
                const events = eventsByDay[dayIdx] || [];

                return (
                  <div key={dayIdx} className="relative border-r last:border-r-0 min-w-0"
                    style={{
                      flex: isExpanded ? expandedFlex : miniFlex,
                      borderColor: T.border,
                      background: dayIdx === 6 ? T.subtle : 'transparent',
                    }}
                    onClick={() => !isExpanded && setExpandedDay(dayIdx)}>
                    {/* Hour grid lines */}
                    {hours.map((_, i) => (
                      <div key={i} className="border-b" style={{
                        height: HOUR_HEIGHT,
                        borderColor: i % 2 === 0 ? T.divider : T.border,
                        background: dayIdx === 2 && i === 1 ? P.primary[2] : 'transparent', // current hour highlight today
                      }}/>
                    ))}

                    {/* Now line on today */}
                    {d.isToday && (
                      <div className="absolute left-0 right-0 z-10 pointer-events-none"
                        style={{ top: HOUR_HEIGHT * 1.5 }}>
                        <div className="h-px" style={{ background: P.tomato[9], boxShadow: `0 0 6px ${P.tomato[9]}` }}/>
                        <div className="absolute -left-1 -top-1 w-2 h-2 rounded-full" style={{ background: P.tomato[9] }}/>
                      </div>
                    )}

                    {/* Events */}
                    {events.map((e, ei) => {
                      const stCfg = STATUS_BG[e.status];
                      const tipoColor = TIPO_DOT[e.tipo];
                      return (
                        <div key={ei} className="absolute left-1 right-1 rounded-md overflow-hidden cursor-pointer transition-all"
                          style={{
                            top: e.h * HOUR_HEIGHT + 2,
                            height: e.dur * HOUR_HEIGHT - 4,
                            background: stCfg.bg,
                            border: `1px solid ${stCfg.bg}`,
                            boxShadow: isExpanded ? '0 1px 3px rgba(0,0,0,0.05)' : 'none',
                          }}>
                          {/* Color stripe (tipo) */}
                          <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: tipoColor }}/>

                          {isExpanded ? (
                            // ---- EXPANDED CARD ----
                            <div className="pl-2 pr-2 py-1.5 h-full flex flex-col">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="font-mono text-[10px] font-semibold tabular-nums" style={{ color: T.ink }}>
                                  {e.hora}
                                </span>
                                <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0 rounded"
                                  style={{ background: stCfg.stripe, color: '#FFF' }}>
                                  {e.status === 'confirmada' ? 'OK' : e.status === 'agendada' ? '?' : e.status === 'realizada' ? '✓' : 'X'}
                                </span>
                              </div>
                              <div className="text-[11px] font-semibold leading-tight truncate" style={{ color: T.ink }}>
                                {e.nome}
                              </div>
                              <div className="flex items-center gap-1 mt-auto">
                                <span className="text-[8px] font-bold uppercase tracking-wider"
                                  style={{ color: tipoColor }}>
                                  {e.tipo}
                                </span>
                                {e.valor !== null && e.pg && (
                                  <span className="ml-auto font-mono text-[9px] tabular-nums font-semibold"
                                    style={{
                                      color: e.pg === 'pago' ? P.jade[11] : e.pg === 'parcial' ? P.amber[11] : P.tomato[11],
                                    }}>
                                    R$ {e.valor}
                                  </span>
                                )}
                              </div>
                            </div>
                          ) : (
                            // ---- MINI CARD ----
                            <div className="pl-1.5 pr-1 py-1 h-full flex flex-col justify-center">
                              <div className="text-[9px] font-semibold leading-tight truncate" style={{ color: T.ink }}>
                                {e.nome}
                              </div>
                              <div className="flex items-center gap-1 mt-0.5">
                                <span className="w-1 h-1 rounded-full" style={{ background: stCfg.stripe }}/>
                                <span className="text-[8px] uppercase font-bold tracking-wider" style={{ color: tipoColor }}>
                                  {e.tipo[0].toUpperCase()}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Empty state for free day */}
                    {events.length === 0 && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-[10px]" style={{ color: T.inkLow }}>livre</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 border-t flex items-center justify-between text-[11px]"
              style={{ borderColor: T.border, background: T.subtle }}>
              <div className="flex items-center gap-3" style={{ color: T.inkLow }}>
                <span>Clique em um dia para expandir</span>
                <span>·</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{ background: P.indigo[9] }}/>Presencial</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{ background: P.cyan[9] }}/>Online</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{ background: P.crimson[9] }}/>Retorno</span>
              </div>
              <div className="flex items-center gap-3" style={{ color: T.inkLow }}>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{ background: P.jade[2], border: `1px solid ${P.jade[9]}` }}/>Confirmada</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{ background: P.amber[2], border: `1px solid ${P.amber[9]}` }}/>Não confirmada</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{ background: P.gray[2], border: `1px solid ${P.gray[9]}` }}/>Realizada</span>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

// ======================= PAGE =======================
export default function AppMockupsRadixV2Page() {
  const [screen, setScreen] = useState('agenda');

  const Screen = {
    agenda: AgendaScreen,
    semana: AgendaSemanaScreen,
    retornos: ControleRetornoScreen,
    modal: NovoLancamentoModal,
  }[screen];

  return (
    <>
      {FONT_LINK}
      <div className="min-h-screen p-6" style={{ background: '#F4F5F7' }}>
        <div className="max-w-[1500px] mx-auto">
          <header className="mb-5">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: '#6B7280' }}>V4 · Radix Colors</span>
              <span className="w-1 h-1 rounded-full" style={{ background: '#9CA3AF' }}/>
              <span className="text-[11px]" style={{ color: '#6B7280' }}>Slate + Iris (primário) + paleta funcional completa</span>
            </div>
            <h1 className="font-geist text-3xl font-semibold tracking-tight" style={{ color: '#0F172A' }}>
              Slate · Iris <span style={{ color: P.primary[9] }}>+ cor funcional</span>
            </h1>
            <p className="text-sm mt-1 max-w-3xl" style={{ color: '#64748B' }}>
              Base neutra com acento roxo-lavanda (Iris) · cores funcionais (Jade, Âmbar, Tomato, Crimson, Indigo, Cyan, Plum) aplicadas em status, tipos de consulta, KPIs e formas de pagamento. Todas da paleta oficial Radix (AAA).
            </p>
          </header>

          {/* Palette showcase */}
          <div className="rounded-xl border p-4 mb-5 bg-white">
            <div className="text-[10px] uppercase tracking-[0.12em] font-bold mb-3" style={{ color: '#64748B' }}>Paleta funcional</div>
            <div className="grid grid-cols-8 gap-3">
              {[
                { n: 'Iris', s: P.primary, u: 'Primário · ações' },
                { n: 'Jade', s: P.jade, u: 'Sucesso · pago · confirmada' },
                { n: 'Amber', s: P.amber, u: 'Warning · parcial' },
                { n: 'Tomato', s: P.tomato, u: 'Erro · cancelada · pendente' },
                { n: 'Indigo', s: P.indigo, u: 'Presencial' },
                { n: 'Crimson', s: P.crimson, u: 'Retorno' },
                { n: 'Cyan', s: P.cyan, u: 'Online' },
                { n: 'Plum', s: P.plum, u: 'Personalizado' },
              ].map((c, i) => (
                <div key={i} className="text-left">
                  <div className="flex gap-0.5 mb-1.5">
                    {[2, 3, 5, 9, 11].map(s => (
                      <div key={s} className="flex-1 h-6 rounded" style={{ background: c.s[s] }}/>
                    ))}
                  </div>
                  <div className="text-[11px] font-semibold" style={{ color: '#0F172A' }}>{c.n}</div>
                  <div className="text-[9px]" style={{ color: '#64748B' }}>{c.u}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Screen tabs */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {[
              { k: 'agenda', label: 'Agenda · Dia' },
              { k: 'semana', label: 'Agenda · Semana' },
              { k: 'retornos', label: 'Controle de Retorno' },
              { k: 'modal', label: 'Modal · Novo Lançamento' },
            ].map(s => (
              <button key={s.k} onClick={() => setScreen(s.k)}
                className="px-3 py-1.5 text-[12px] font-medium rounded-md transition-all border"
                style={screen === s.k
                  ? { background: P.primary[9], color: '#FFF', borderColor: P.primary[9] }
                  : { background: '#FFF', color: '#475569', borderColor: '#E2E8F0' }}>
                {s.label}
              </button>
            ))}
          </div>

          <div className="rounded-2xl overflow-hidden shadow-2xl"
            style={{ border: '8px solid #0F172A', background: '#F1F5F9', height: 760 }}>
            <Screen />
          </div>

          <p className="text-xs mt-4 text-center" style={{ color: '#94A3B8' }}>
            Mesmo DNA Slate · Iris — agora com cor aplicada onde traz valor funcional · 100% tokens Radix · 100% Shadcn/UI
          </p>
        </div>
      </div>
    </>
  );
}
